import type { AuthError, EmailOtpType, Session } from "@supabase/supabase-js"

/**
 * What the link in a Supabase Auth email (the password reset) brought to the
 * page it opened:
 *
 * - `tokenHash`: the new links (`#token_hash=…&type=…` in the fragment; `?token_hash=…` in older emails). They lead straight
 *   here, on our domain, and the page verifies the token itself (see
 *   verifyAuthEmailLinkOnce).
 * - The old links went to Supabase's `/auth/v1/verify`, which verified the
 *   token and came back with the session in the fragment (picked up by
 *   supabase-js), or with `error_code` (e.g. `otp_expired`).
 *
 * NOTE: Emails sent before the templates changed still carry the old links,
 * so both kinds stay supported.
 */
type AuthEmailLink =
  | { kind: "tokenHash"; tokenHash: string; type: EmailOtpType | undefined }
  | { kind: "error"; code: string }
  | { kind: "none" }

const EMAIL_OTP_TYPES: readonly EmailOtpType[] = [
  "email",
  "email_change",
  "invite",
  "magiclink",
  "recovery",
  "signup",
]

const isEmailOtpType = (value: string | undefined): value is EmailOtpType =>
  EMAIL_OTP_TYPES.includes(value as EmailOtpType)

/**
 * A parameter of the query or the fragment.
 *
 * NOTE: Found anywhere after the first `?` or `#`, not only through
 * URLSearchParams: an email template appending `?token_hash=…` to a redirect
 * URL that has a query already makes a second `?`.
 */
function findParam(url: string, name: string): string | undefined {
  const match = url.match(new RegExp(`[?&#]${name}=([^&#]*)`))
  if (!match) return undefined
  try {
    return decodeURIComponent(match[1].replace(/\+/g, " ")) || undefined
  } catch {
    return undefined
  }
}

export function parseAuthEmailLink(
  url: string | null | undefined,
): AuthEmailLink {
  if (!url) return { kind: "none" }

  const tokenHash = findParam(url, "token_hash")
  if (tokenHash) {
    const type = findParam(url, "type")
    return {
      kind: "tokenHash",
      tokenHash,
      type: isEmailOtpType(type) ? type : undefined,
    }
  }

  const errorCode = findParam(url, "error_code") ?? findParam(url, "error")
  if (errorCode) return { kind: "error", code: errorCode }

  return { kind: "none" }
}

declare global {
  interface Window {
    /** The address the app was opened with, see index.html. */
    flLandingUrl?: string
  }
}

/**
 * The auth email link that opened the app on `pathname`, or `none` (opened on
 * another page, or no link).
 *
 * NOTE: Read from the address the app was opened with (saved in index.html,
 * which also takes a `token_hash` out of the address bar before
 * Sentry or supabase-js read it).
 */
export function authEmailLinkFor(pathname: string): AuthEmailLink {
  const landingUrl = window.flLandingUrl ?? window.location.href
  let landingPathname: string
  try {
    landingPathname = new URL(landingUrl).pathname
  } catch {
    return { kind: "none" }
  }
  const trim = (path: string) => path.replace(/\/+$/, "")
  if (trim(landingPathname) !== trim(pathname)) return { kind: "none" }
  return parseAuthEmailLink(landingUrl)
}

type VerifyResult = { session: Session | null; error: AuthError | null }
type VerifyOtp = (params: {
  token_hash: string
  type: EmailOtpType
}) => Promise<{ data: { session: Session | null }; error: AuthError | null }>

const verifications = new Map<string, Promise<VerifyResult>>()

/**
 * Verifies a `token_hash` link, once per token: a token works only once, and
 * React (StrictMode, or coming back to the page) may ask again.
 */
export function verifyAuthEmailLinkOnce(
  verifyOtp: VerifyOtp,
  tokenHash: string,
  type: EmailOtpType,
): Promise<VerifyResult> {
  const key = `${type}:${tokenHash}`
  let verification = verifications.get(key)
  if (!verification) {
    verification = verifyOtp({ token_hash: tokenHash, type }).then(
      ({ data, error }) => ({ session: data.session, error }),
    )
    verifications.set(key, verification)
  }
  return verification
}
